import { CHART_SERIES_COLORS, MAIN_AXIS_UNITS } from '@/features/telemetry/config';
import type { TelemetryField, TelemetryResolution } from '@/features/telemetry/services/schemas';
import { enumMetricKey, numericMetricKey } from '@/features/telemetry/utils/telemetryKeys';
import { toDisplayUnit } from '@/features/telemetry/utils/unitDisplay';

/** Case-insensitive check against MAIN_AXIS_UNITS ('w', 'kw'). */
function isMainAxisUnit(unit: string | null): boolean {
  return unit !== null && (MAIN_AXIS_UNITS as readonly string[]).includes(unit.toLowerCase());
}

export interface SeriesConfig {
  variable: string;
  key: string;
  label: string;
  unit: string | null;
  color: string;
  axis: 'left' | 'right';
}

export interface StatusChannelConfig {
  variable: string;
  key: string;
  label: string;
}

export interface DeviceMetrics {
  series: SeriesConfig[];
  channels: StatusChannelConfig[];
}

const STATUS_VALUE_TYPES = new Set(['string', 'bool']);

/**
 * A field whose bucket values are raw opaque status strings, not numbers to plot —
 * either it declares real enum/state choices, or its value type says so outright.
 *
 * `enumChoices` is observed as `null` on the wire for fields with no enum values
 * (the OpenAPI schema says `type: object`, but the live backend sends `null`).
 */
function isStatusField(field: TelemetryField): boolean {
  const hasEnumChoices = field.enumChoices !== null && Object.keys(field.enumChoices).length > 0;
  return hasEnumChoices || STATUS_VALUE_TYPES.has(field.valueType);
}

/** Everything else that's numerically summarizable belongs on the chart as a series. */
function isNumericField(field: TelemetryField): boolean {
  return field.aggregatable && !isStatusField(field);
}

/**
 * Resolves a telemetry field catalog into the concrete numeric series and status
 * channels for one device type, at one resolution — the bridge between the API's
 * backend-driven field catalog and what the chart/timeline components render.
 *
 * No device type or metric name is ever hardcoded: a field's classification comes
 * entirely from the catalog's own `enumChoices`/`aggregatable`/`unit`, so a new device
 * type or metric works without a code change here. Axis placement has one intentional
 * unit preference (below) — everything else follows from the catalog.
 */
export function toDeviceMetrics(
  fields: TelemetryField[],
  deviceType: string,
  resolution: TelemetryResolution
): DeviceMetrics {
  const fieldsForType = fields.filter((field) => field.deviceType === deviceType);
  const numericFields = fieldsForType.filter(isNumericField);

  // Each field's display unit, computed once and reused for both axis placement and
  // rendering (chart legend, axis label, tooltip all read this same mapped value).
  const units = numericFields.map((field) => toDisplayUnit(field.unit));

  // A power unit (W/kW), if this device has one, anchors the primary (left) axis —
  // power is almost always the metric the chart is really about, e.g. a battery's kW
  // curve should anchor the main axis with its SOC% pushed to the secondary one, not
  // the reverse. With no power unit present, the first series becomes primary instead,
  // so every device still gets one metric on the main axis.
  const hasMainAxisUnit = units.some(isMainAxisUnit);

  const series: SeriesConfig[] = numericFields.map((field, index) => {
    const unit = units[index];
    const axis: SeriesConfig['axis'] = hasMainAxisUnit
      ? isMainAxisUnit(unit)
        ? 'left'
        : 'right'
      : index === 0
        ? 'left'
        : 'right';

    return {
      variable: field.variable,
      key: numericMetricKey(field.variable, resolution),
      label: field.label,
      unit,
      color: CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length],
      axis,
    };
  });

  const channels: StatusChannelConfig[] = fieldsForType.filter(isStatusField).map((field) => ({
    variable: field.variable,
    key: enumMetricKey(field.variable, resolution),
    label: field.label,
  }));

  return { series, channels };
}
